<template>
  <div class="fsearch">
    <div class="fsearch-inputwrap">
      <span class="fsearch-icon" aria-hidden="true">🔍</span>
      <input ref="input" type="text" class="fsearch-input"
             :placeholder="placeholder"
             v-model="q"
             role="combobox" :aria-expanded="open ? 'true' : 'false'" aria-autocomplete="list"
             @input="onInput"
             @focus="onFocus"
             @blur="onBlur"
             @keydown.down.prevent="move(1)"
             @keydown.up.prevent="move(-1)"
             @keydown.enter.prevent="enter"
             @keydown.esc="close" />
      <button v-if="q" type="button" class="fsearch-clear" @mousedown.prevent="clearInput" aria-label="Clear search">×</button>
    </div>
    <div v-if="open" class="fsearch-dropdown" role="listbox">
      <div v-if="loading" class="fsearch-state">Searching…</div>
      <div v-else-if="error" class="fsearch-state">Couldn't search — please try again.</div>
      <div v-else-if="!groups.length" class="fsearch-state">No matching filters found.</div>
      <template v-else>
        <div v-for="g in groups" :key="g.type" class="fsearch-group">
          <div class="fsearch-grouplabel">{{ g.label }}</div>
          <div v-for="it in g.items" :key="g.type + '|' + it.id"
               class="fsearch-item" :class="{ active: flat[activeIndex] === it }"
               role="option" :aria-selected="flat[activeIndex] === it ? 'true' : 'false'"
               @mousedown.prevent="select(it)"
               @mouseenter="activeIndex = flat.indexOf(it)">
            <span class="fsearch-pri" v-html="highlight(it.primaryText)"></span>
            <span v-if="it.secondaryText" class="fsearch-sec" v-html="highlight(it.secondaryText)"></span>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import httpClient from "@/assets/js/HttpClient";

export default {
  name: "FilterSearch",
  props: {
    placeholder: { type: String, default: "Search customers, parts, territories, reps, orders…" },
  },
  data() {
    return { q: "", open: false, loading: false, error: false, groups: [], activeIndex: -1, reqSeq: 0, searchTimer: null };
  },
  computed: {
    // Flat list across all groups, for keyboard navigation.
    flat() {
      const out = [];
      (this.groups || []).forEach((g) => (g.items || []).forEach((it) => out.push(it)));
      return out;
    },
  },
  methods: {
    onInput() {
      this.open = true;
      if (this.searchTimer) clearTimeout(this.searchTimer);
      const term = (this.q || "").trim();
      if (term.length < 2) { this.groups = []; this.loading = false; this.error = false; return; }
      this.searchTimer = setTimeout(() => this.search(term), 300);   // debounce
    },
    onFocus() { if ((this.q || "").trim().length >= 2 && this.groups.length) this.open = true; },
    onBlur() { setTimeout(() => { this.open = false; }, 120); },   // allow item mousedown to land first
    search(term) {
      const rid = ++this.reqSeq;
      this.loading = true; this.error = false;
      httpClient.get("/report/filter-suggestions", { params: { q: term, limit: 6 } })
        .then((res) => {
          if (rid !== this.reqSeq) return;   // stale response — a newer search started
          this.groups = (res.data && res.data.groups) || [];
          this.activeIndex = this.flat.length ? 0 : -1;
          this.loading = false;
        })
        .catch(() => {
          if (rid !== this.reqSeq) return;
          this.error = true; this.groups = []; this.loading = false;
        });
    },
    move(dir) {
      const n = this.flat.length;
      if (!this.open || !n) return;
      this.activeIndex = (this.activeIndex + dir + n) % n;
    },
    enter() {
      if (this.open && this.activeIndex >= 0 && this.flat[this.activeIndex]) this.select(this.flat[this.activeIndex]);
    },
    select(it) {
      this.$emit("select", it);
      this.q = ""; this.groups = []; this.open = false; this.activeIndex = -1;
    },
    clearInput() {
      this.q = ""; this.groups = []; this.open = false;
      this.$nextTick(() => { if (this.$refs.input) this.$refs.input.focus(); });
    },
    close() { this.open = false; },
    highlight(text) {
      const esc = String(text == null ? "" : text).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      const term = (this.q || "").trim();
      if (!term) return esc;
      const i = esc.toLowerCase().indexOf(term.toLowerCase());
      if (i < 0) return esc;
      return esc.slice(0, i) + "<mark>" + esc.slice(i, i + term.length) + "</mark>" + esc.slice(i + term.length);
    },
  },
  beforeDestroy() { if (this.searchTimer) clearTimeout(this.searchTimer); },
};
</script>

<style scoped>
.fsearch { position: relative; flex: 1 1 320px; min-width: 0; }
.fsearch-inputwrap { position: relative; display: flex; align-items: center; }
.fsearch-icon { position: absolute; left: 11px; font-size: 13px; opacity: .55; pointer-events: none; }
.fsearch-input { width: 100%; height: 40px; border: 1px solid #d4d4d8; border-radius: 8px; padding: 0 32px 0 32px; font-size: 14px; background: #fff; }
.fsearch-input:focus { outline: none; border-color: #9e2436; box-shadow: 0 0 0 2px rgba(158,36,54,.12); }
.fsearch-clear { position: absolute; right: 8px; border: 0; background: #ececef; color: #555; width: 20px; height: 20px; border-radius: 50%; cursor: pointer; font-size: 13px; line-height: 1; }
.fsearch-clear:hover { background: #9e2436; color: #fff; }
.fsearch-dropdown { position: absolute; top: calc(100% + 4px); left: 0; right: 0; background: #fff; border: 1px solid #e0e0e4; border-radius: 8px; box-shadow: 0 8px 24px rgba(0,0,0,.16); z-index: 60; max-height: 360px; overflow-y: auto; padding: 4px 0; }
.fsearch-state { padding: 12px 14px; color: #888; font-size: 13px; }
.fsearch-grouplabel { font-size: 10.5px; font-weight: 800; text-transform: uppercase; letter-spacing: .04em; color: #9e2436; padding: 9px 14px 3px; }
.fsearch-item { display: flex; flex-direction: column; padding: 6px 14px; cursor: pointer; }
.fsearch-item.active { background: #fdecee; }
.fsearch-pri { font-size: 13px; color: #222; font-weight: 600; line-height: 1.2; }
.fsearch-sec { font-size: 11.5px; color: #888; }
.fsearch-pri >>> mark, .fsearch-sec >>> mark { background: #ffe27a; color: inherit; padding: 0 1px; border-radius: 2px; }
</style>
